import { Outlet } from 'react-router-dom';

import { AppLoader } from '@/shared/components/feedback/AppLoader';
import { SessionUnavailable } from '@/shared/components/feedback/SessionUnavailable';

import { SignOutFirst } from '../components/SignOutFirst';
import { useAuth } from '../session';

/**
 * Stands in front of an emailed link whose validity cannot be known yet, while somebody is
 * signed in.
 *
 * <h2>One route uses this now, and the reason is the whole of `AC-63`</h2>
 *
 * It guarded all three token-carrying links until 4 October 2026. A guard decides **before**
 * the page it guards renders, so it necessarily decides before anything can ask whether the
 * link works — and for `/activate/set-password`, which can ask without spending the link,
 * that was the wrong order. The symptom was a screen that oscillated:
 *
 * <pre>
 *   invalid invitation, signed out   ->  "This invitation link is no longer valid"
 *   another tab signs in             ->  "Someone is already signed in"
 *   that tab signs out               ->  "This invitation link is no longer valid"
 * </pre>
 *
 * <p>Nothing about the link changed. The invitation was dead throughout, and the screen
 * said so, then stopped saying so, then said so again — so the one fact the person needed
 * looked as though it depended on who was signed in. It does not: a dead link is dead for
 * everybody, and `AC-63` makes link validity outrank the session.
 *
 * <p>That page therefore owns its own chain now, with this same conflict screen at step 6
 * of it. `/activate` owns one too, for the opposite reason: it cannot ask the question
 * without spending the link, so the session still comes first there — `AC-63a`.
 *
 * <h2>Which leaves `/reset-password`</h2>
 *
 * It has no non-consuming check. Its token is validated by the **submit**, so on arrival
 * the link's state is genuinely unknown and there is nothing for link validity to outrank —
 * the precedence question does not arise. A guard is exactly right in front of it, and
 * keeping it here means the routing table still shows at a glance that the route is not for
 * a signed-in browser.
 *
 * <h2>Why it is not simply redirected</h2>
 *
 * `GuestRoute` sends a signed-in person from `/login` or `/signup` to the dashboard,
 * silently, and that is right: nothing is lost. An emailed link carries a **single-use
 * token**, so a silent redirect swallows it — the person goes back to their inbox, clicks
 * the same link, lands on the dashboard again, and the link looks broken when it never was.
 *
 * <h2>Why it is not signed out for them either</h2>
 *
 * The obvious alternative — end the old session and carry on — ends a session that may be
 * in the middle of something. An administrator with a half-filled form in another tab loses
 * it, on a decision made by whoever opened the link, which is usually not them. Nothing
 * here is destroyed: the screen states who is signed in and hands the choice to the person
 * at the keyboard, which is what Google, GitHub and Atlassian do with an invitation opened
 * in a signed-in browser.
 *
 * <h2>Nothing navigates after the sign-out</h2>
 *
 * The URL never changed, token and all, so the moment there is no user this guard renders
 * the page underneath it. Sending them anywhere and back would be a round trip to arrive
 * where they already are — and any redirect risks dropping the query string, which is the
 * one part that cannot be recovered.
 *
 * <h2>It waits for the session, and the wait can end</h2>
 *
 * `status` is the application's one answer to "is somebody signed in", shared with
 * `ProtectedRoute` and `GuestRoute` — `unknown` while `/account/me` is in flight, and only
 * then `signed-in` or `signed-out`. Acting on the wait as though it were a refusal would
 * flash the form at somebody who is about to be told to sign out.
 *
 * <p>But `unknown` is not always temporary: a token in storage plus an unanswerable
 * `/account/me` stays `unknown` for ever, because nothing retries it on its own. This
 * rendered the loader for that until `RM-21g`, so a link opened while the backend was down
 * sat on a spinner reading "Loading" with nothing to press.
 */
export function SignOutFirstRoute() {
  const { user, status, sessionUnreachable, retrySession } = useAuth();

  /*
   * BEFORE the `unknown` branch, because this IS an `unknown` that will never resolve.
   *
   * Not a refusal, so the token stays and nobody is signed out over a backend restart --
   * and nothing navigates, so the link in the address bar survives being told this and is
   * still there when `Try again` succeeds.
   */
  if (sessionUnreachable) {
    return <SessionUnavailable onRetry={retrySession} />;
  }

  if (status === 'unknown') {
    return <AppLoader />;
  }

  if (status === 'signed-in' && user) {
    return <SignOutFirst email={user.email} />;
  }

  return <Outlet />;
}
